import React, { useMemo, useState } from "react";
import { cloneDeep } from "lodash";
import { Box, Button, List, ListItem, ListItemText, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { PathFundSelection } from "../../../hooks/feeLab/useNewProductHandlers";
import { MimshakProductUtil } from "../../../services/MimshakProductUtil";
import { fundService } from "../../../services/fundService";
import { formatCurrency } from "../../../utils/namesFormater";
import SelectNewPathDrawer from "../../FeesLab/SelectNewPathDrawer";
import { fundLabel, policyLabel } from "./quickActions";

// the drawer is opened from a dialog - above it
const DRAWER_Z_INDEX = 1400;

type SplitMode = "single" | "split";

const rightAlignedText = { primary: { style: { textAlign: "right" as const } }, secondary: { style: { textAlign: "right" as const } } };

/**
 * One new path: the drawer gets the policy as if it had a single path holding all the balance - choosing a fund
 * replaces it at once, with all the money (the lab's single-path behavior).
 */
const asSinglePath = (policy: MimshakData): MimshakData => {
  const single = cloneDeep(policy);
  (single.data?.products ?? []).forEach((product) =>
    (product.policies_or_accounts ?? []).forEach((account) => {
      if (account.policy_number !== policy.policy_number || !account.investment_paths?.length) return;
      const largest = account.investment_paths.reduce((a, b) => ((b.accumulated_amount ?? 0) > (a.accumulated_amount ?? 0) ? b : a));
      account.investment_paths = [{ ...largest, accumulated_amount: policy.funds_sum || 0, deposit_percentage: undefined }];
    })
  );
  return single;
};

interface Props {
  /** the policy whose paths are chosen - its handler's funds are offered, its balance is split between the paths */
  policy: MimshakData;
  selections: PathFundSelection[];
  onSelectionsChange: (selections: PathFundSelection[]) => void;
  /** show the policy's current paths (not for a new fund) */
  showCurrentPaths?: boolean;
}

/**
 * בחירת מסלולים לקופה: מסלול אחד חדש, או פיצול למספר מסלולים – במגירת בחירת המסלול של המעבדה,
 * מסוננת ליצרן של הקופה.
 */
const PathChooser: React.FC<Props> = ({ policy, selections, onSelectionsChange, showCurrentPaths = true }) => {
  const [splitMode, setSplitMode] = useState<SplitMode>("single");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const currentPaths = useMemo(
    () =>
      (policy.data?.products ?? [])
        .flatMap((p) => MimshakProductUtil.getMergedPathsFromProduct(p))
        .map((p) => ({
          pathCode: p.path_code,
          pathName: p.path_name ?? "",
          amount: p.accumulated_amount ?? 0,
          fund: fundService.getFundFromCacheById(p.path_code) ?? null,
        }))
        .filter((p) => p.fund),
    [policy]
  );

  // memoized - the path selection re-reads its paths whenever the policy object changes
  const drawerMimshak = useMemo(() => (splitMode === "single" ? asSinglePath(policy) : policy), [policy, splitMode]);

  const changeSplitMode = (mode: SplitMode) => {
    setSplitMode(mode);
    onSelectionsChange([]);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {showCurrentPaths && (
        <Box>
          <Typography variant="subtitle2">מסלול נוכחי</Typography>
          <Typography variant="body2" color="text.secondary">
            {currentPaths.length > 0 ? currentPaths.map((p) => fundLabel(p.fund!)).join(", ") : "לא נבחר מסלול"}
          </Typography>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          איך?
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={splitMode} onChange={(_e, v) => v && changeSplitMode(v)}>
          <ToggleButton value="single">מסלול אחד</ToggleButton>
          <ToggleButton value="split">פיצול למספר מסלולים</ToggleButton>
        </ToggleButtonGroup>
        {splitMode === "split" && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            במגירה: הוסף מסלולים, קבע את הסכום לכל מסלול{showCurrentPaths ? " (את המסלול הנוכחי אפשר להסיר ב-X)" : ""}, וסגור את
            המגירה
          </Typography>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2">{splitMode === "split" ? "מסלולים חדשים" : "מסלול חדש"}</Typography>
        {selections.length > 0 ? (
          <List dense disablePadding>
            {selections.map((s) => (
              <ListItem key={s.fund.fund_id} disableGutters>
                <ListItemText
                  primary={fundLabel(s.fund)}
                  secondary={[
                    s.amount ? formatCurrency(s.amount) : null,
                    s.depositPercentage != null ? `${s.depositPercentage}% מההפקדה` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  slotProps={rightAlignedText}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            עדיין לא נבחר
          </Typography>
        )}
        <Button size="small" variant="outlined" onClick={() => setDrawerOpen(true)} sx={{ mt: 1 }}>
          {selections.length > 0 ? "שנה" : splitMode === "split" ? "בחר מסלולים" : "בחר מסלול"}
        </Button>
      </Box>

      {drawerOpen && (
        <SelectNewPathDrawer
          open
          onClose={() => setDrawerOpen(false)}
          mimshak={drawerMimshak}
          originalMimshak={policy}
          paths={currentPaths}
          title={`${splitMode === "split" ? "פיצול למסלולים" : "בחירת מסלול"} – ${policyLabel(policy)}`}
          onFundsSelected={(chosen) => {
            onSelectionsChange(chosen);
            setDrawerOpen(false);
          }}
          zIndex={DRAWER_Z_INDEX}
          startInAddMode={splitMode === "split"}
        />
      )}
    </Box>
  );
};

export default PathChooser;
